/**
 * Règles de rôles, partagées par l'API et le front pour éviter toute divergence.
 *
 * Un compte peut porter plusieurs rôles : l'accès est accordé si **au moins un**
 * rôle satisfait l'exigence, et les périmètres se cumulent en union.
 */

import { UserRole } from '../enums/index.js';

/**
 * Rôles non bornés par un périmètre métier : ils voient toutes les familles.
 * Seul CONSEILLER est cloisonné.
 */
export const ROLES_TRANSVERSES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.ANALYSTE,
  UserRole.OBSERVATEUR,
];

/**
 * Ordre de priorité décroissant. Détermine le rôle principal d'un compte
 * multi-rôle, donc son espace d'accueil.
 */
const PRIORITE: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.ANALYSTE,
  UserRole.CONSEILLER,
  UserRole.OBSERVATEUR,
];

/** Espace d'accueil par rôle principal. */
const ACCUEIL: Record<UserRole, string> = {
  [UserRole.ADMIN]: '/administration',
  [UserRole.ANALYSTE]: '/analyses',
  [UserRole.CONSEILLER]: '/exploration',
  [UserRole.OBSERVATEUR]: '/tableau-de-bord',
};

/**
 * Combinaisons de rôles autorisées. Toute autre combinaison est refusée à la
 * création comme à la modification d'un compte.
 *
 * OBSERVATEUR n'est jamais cumulé : c'est un rôle de lecture seule, que tout
 * autre rôle englobe déjà.
 */
const COMBINAISONS_AUTORISEES: readonly (readonly UserRole[])[] = [
  [UserRole.ADMIN],
  [UserRole.ANALYSTE],
  [UserRole.CONSEILLER],
  [UserRole.OBSERVATEUR],
  [UserRole.ADMIN, UserRole.ANALYSTE],
  [UserRole.ANALYSTE, UserRole.CONSEILLER],
];

const cle = (roles: readonly UserRole[]) => [...new Set(roles)].sort().join('|');

const COMBINAISONS_VALIDES = new Set(COMBINAISONS_AUTORISEES.map(cle));

/** Vrai si l'ensemble de rôles fourni fait partie des combinaisons autorisées. */
export function validateRoleSet(roles: readonly UserRole[]): boolean {
  if (!roles.length) return false;
  return COMBINAISONS_VALIDES.has(cle(roles));
}

/** Rôle principal d'un compte : le plus élevé selon l'ordre de priorité. */
export function resolvePrimaryRole(roles: readonly UserRole[]): UserRole {
  for (const role of PRIORITE) {
    if (roles.includes(role)) return role;
  }
  return UserRole.OBSERVATEUR;
}

/** Chemin d'accueil déduit des rôles du compte. */
export function resolveHome(roles: readonly UserRole[]): string {
  return ACCUEIL[resolvePrimaryRole(roles)];
}

/** Vrai si le compte échappe au cloisonnement par famille. */
export function isTransverse(roles: readonly UserRole[]): boolean {
  return roles.some((r) => ROLES_TRANSVERSES.includes(r));
}

/** Vrai si le compte porte au moins un des rôles requis. */
export function hasAnyRole(roles: readonly UserRole[], requis: readonly UserRole[]): boolean {
  return requis.some((r) => roles.includes(r));
}

/** Libellés d'affichage. */
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Administrateur',
  [UserRole.ANALYSTE]: 'Analyste',
  [UserRole.CONSEILLER]: 'Conseiller',
  [UserRole.OBSERVATEUR]: 'Observateur',
};
